import type { Patient, Reference } from '@medplum/fhirtypes'
import type { StandardTask } from '@seen/core/models/tasks/standard/standardTask'
import type { ReactNode } from 'react'
import { TaskRow } from '#src/components/task/list/TaskRow.tsx'
import { TaskListParticipantGroupHeader } from '#src/components/TaskListParticipantGroupHeader.tsx'
import { TaskListStateGroupHeader } from '#src/components/TaskListStateGroupHeader.tsx'
import type { TaskComposerDefaultValues } from '#src/hooks/useTaskComposerStore.ts'
import type {
  TaskListGrouping,
  TaskListItem,
} from '#src/hooks/useTaskListItems.ts'

export interface TaskListRowDisplay {
  showPriority?: boolean
  showLabels?: boolean
  showDueDate?: boolean
  showAuthoredOn?: boolean
  showOwner?: boolean
}

export interface TaskListRowProps {
  item: TaskListItem
  defaultValues?: TaskComposerDefaultValues
  participant?: Reference<Patient>
  tasks?: StandardTask[]
  selectedTasks?: Map<string, StandardTask>
  setSelectedTasks?: (tasks: Map<string, StandardTask>) => void
  grouping?: TaskListGrouping
  hideOrder?: boolean
  display?: TaskListRowDisplay
  onTaskCheckedChange?: (
    task: StandardTask,
    value: boolean,
    isShiftClick?: boolean
  ) => void
}

export function TaskListRow({
  item,
  defaultValues,
  participant,
  tasks,
  selectedTasks,
  setSelectedTasks,
  grouping,
  hideOrder,
  display,
  onTaskCheckedChange,
}: TaskListRowProps): ReactNode {
  const hideCheckboxes = !tasks || !selectedTasks || !setSelectedTasks

  if (item.type === 'state-group') {
    const tasksOfType = tasks?.filter((t) => t.state === item.state)
    const allSelected = tasksOfType?.every((t) => selectedTasks?.has(t.id))
    return (
      <TaskListStateGroupHeader
        state={item.state}
        defaultValues={defaultValues}
        participant={participant}
        checked={allSelected}
        hideCheckbox={hideCheckboxes}
        onCheckedChange={() => {
          if (allSelected) {
            tasksOfType?.forEach((t) => {
              selectedTasks?.delete(t.id)
            })
            setSelectedTasks?.(new Map(selectedTasks))
          } else {
            tasksOfType?.forEach((t) => {
              selectedTasks?.set(t.id, t)
            })
            setSelectedTasks?.(new Map(selectedTasks))
          }
        }}
      />
    )
  }

  if (item.type === 'participant-group') {
    const tasksOfType = tasks?.filter(
      (t) =>
        t.participant?.reference === item.participant ||
        (!t.participant && !item.participant)
    )
    const allSelected = tasksOfType?.every((t) => selectedTasks?.has(t.id))
    return (
      <TaskListParticipantGroupHeader
        participant={item.participant}
        defaultValues={defaultValues}
        checked={allSelected}
        hideCheckbox={hideCheckboxes}
        onCheckedChange={() => {
          if (allSelected) {
            tasksOfType?.forEach((t) => {
              selectedTasks?.delete(t.id)
            })
            setSelectedTasks?.(new Map(selectedTasks))
          } else {
            tasksOfType?.forEach((t) => {
              selectedTasks?.set(t.id, t)
            })
            setSelectedTasks?.(new Map(selectedTasks))
          }
        }}
      />
    )
  }

  const checked = selectedTasks?.has(item.task.id)
  return (
    <TaskRow
      task={item.task}
      hidePriority={display?.showPriority === false}
      hideParticipant={!!participant || grouping === 'participant'}
      hideOrder={hideOrder}
      hideAuthoredOn={display?.showAuthoredOn === false}
      hideLabels={display?.showLabels === false}
      hideDueDate={display?.showDueDate === false}
      hideOwner={display?.showOwner === false}
      checked={checked}
      hideCheckbox={hideCheckboxes}
      onCheckedChange={(value, isShiftClick) => {
        if (onTaskCheckedChange) {
          onTaskCheckedChange(item.task, value, isShiftClick)
        } else {
          if (value) {
            selectedTasks?.set(item.task.id, item.task)
            setSelectedTasks?.(new Map(selectedTasks))
          } else {
            selectedTasks?.delete(item.task.id)
            setSelectedTasks?.(new Map(selectedTasks))
          }
        }
      }}
    />
  )
}
